import { type MaybeComputedElementRef, unrefElement } from '@vueuse/core';
import { nextTick } from 'vue';

function takesFocus(control: HTMLElement) {
  control.focus();
  return document.activeElement === control;
}

/**
 * Focuses the first control marked `aria-invalid="true"` inside `form`, in DOM
 * order, once Vue has rendered the errors that a failed `v$.$touch()` produced.
 * Call it when a submit is rejected so keyboard and screen reader users land on
 * the first error instead of staying on the submit button. Bitts inputs, selects
 * and multiselects put `aria-invalid` on their focusable control. A control that
 * cannot take focus, such as one inside a closed collapse, is skipped.
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/error-identification.html (2026-10-14)
 */
export async function focusFirstInvalidField(form: MaybeComputedElementRef) {
  await nextTick();
  const formElement = unrefElement(form);
  if (!(formElement instanceof Element)) return;
  const invalidControls = formElement.querySelectorAll<HTMLElement>(
    '[aria-invalid="true"]',
  );
  Array.from(invalidControls).some(takesFocus);
}
